import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../../context/AuthContext';
import api from '../../../../../services/api';
import { getDistanceLabel } from '../../../utils/distanceUtils';
import CustomerSection from '../CreateTaskModal/CustomerSection';
import LocationSection from '../CreateTaskModal/LocationSection';
import ImageUpload from '../CreateTaskModal/ImageUpload';
import DateTimeInput from '../CreateTaskModal/DateTimeInput';
import './CreateSubtaskModal.css';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import '../TaskModals.css';

const CreateSubtaskModal = ({
  isOpen,
  onClose,
  onSubmit,
  parentTask,
  departments = [],
  employees = [],
  customers = [],
  employeeLocations = [],
  isSubmitting = false
}) => {
  const { company } = useAuth();
  const [subtaskForm, setSubtaskForm] = useState({
    description: '',
    customerId: '',
    departmentId: '',
    employeeId: '',
    supervisorId: '',
    priority: 'medium',
    dueDate: '',
    supervisorNotes: '',
    location: {
      coordinates: {
        lat: null,
        lng: null
      },
      address: ''
    },
    images: [],
    followUpOn: ''
  });

  // Customer section is always disabled for subtasks since they inherit from parent
  const [newCustomerMode, setNewCustomerMode] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isLoadingSupervisors, setIsLoadingSupervisors] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const toLocalDateTimeInput = (value) => {
    try {
      if (!value) return '';
      // If already in local input format (YYYY-MM-DDTHH:mm)
      if (typeof value === 'string' && value.includes('T') && value.length >= 16 && !value.endsWith('Z')) {
        return value.slice(0, 16);
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      const tzOffsetMinutes = date.getTimezoneOffset();
      const localMillis = date.getTime() - tzOffsetMinutes * 60000;
      return new Date(localMillis).toISOString().slice(0, 16);
    } catch (_) {
      return '';
    }
  };

  // Fetch supervisors when component mounts or company changes
  useEffect(() => {
    if (company?.id && isOpen) {
      fetchSupervisors();
    }
  }, [company?.id, isOpen]);

  // Initialize form with parent task data when modal opens
  useEffect(() => {
    if (isOpen && parentTask) {
      setSubtaskForm({
        description: '',
        customerId: parentTask.customer?.id || '',
        departmentId: parentTask.department?.id || '',
        employeeId: '',
        supervisorId: '',
        priority: 'medium',
        dueDate: '',
        supervisorNotes: '',
        location: parentTask.location ? {
          coordinates: {
            lat: parentTask.location.coordinates?.lat || null,
            lng: parentTask.location.coordinates?.lng || null
          },
          address: parentTask.location.address || ''
        } : {
          coordinates: { lat: null, lng: null },
          address: ''
        },
        images: [],
        followUpOn: ''
      });
    }
  }, [isOpen, parentTask]);

  // Filter employees and reset supervisor when department changes
  useEffect(() => {
    if (subtaskForm.departmentId) {
      filterEmployeesByDepartment(subtaskForm.departmentId);
      // Reset supervisor and employee when department changes
      setSubtaskForm(prev => ({
        ...prev,
        supervisorId: '',
        employeeId: ''
      }));
    } else {
      setFilteredEmployees([]);
    }
  }, [subtaskForm.departmentId, employees]);

  const fetchSupervisors = async () => {
    setIsLoadingSupervisors(true);
    try {
      const response = await api.get('/api/companies/users');
      if (response.data?.is_success) {
        // Filter for supervisors only - they are Users with role admin or supervisor
        const supervisorList = response.data.users.filter(user =>
          (user.role === 'admin' || user.role === 'supervisor') && user.isActive
        );
        setSupervisors(supervisorList);
      }
    } catch (error) {
      console.error('Error fetching supervisors:', error);
    } finally {
      setIsLoadingSupervisors(false);
    }
  };

  const filterEmployeesByDepartment = (departmentId) => {
    setIsLoadingEmployees(true);
    if (!departmentId) {
      setFilteredEmployees([]);
      setIsLoadingEmployees(false);
      return;
    }

    // Filter employees who belong to the selected department
    const filtered = employees.filter(emp =>
      emp.departments && emp.departments.some(dept => dept.id == departmentId)
    );
    setFilteredEmployees(filtered);
    setIsLoadingEmployees(false);
  };

  const getFilteredSupervisors = () => {
    if (!subtaskForm.departmentId) return [];

    return supervisors.filter(sup => {
      // If supervisor is admin, show in all teams
      if (sup.role === 'admin') {
        return true;
      }
      // Otherwise, filter by department assignment
      return sup.departments && sup.departments.some(dept =>
        dept.id === parseInt(subtaskForm.departmentId)
      );
    });
  };

  const resetForm = () => {
    if (parentTask) {
      setSubtaskForm({
        description: '',
        customerId: parentTask.customer?.id || '',
        departmentId: parentTask.department?.id || '',
        employeeId: '',
        supervisorId: '',
        priority: 'medium',
        dueDate: '',
        supervisorNotes: '',
        location: parentTask.location ? {
          coordinates: {
            lat: parentTask.location.coordinates?.lat || null,
            lng: parentTask.location.coordinates?.lng || null
          },
          address: parentTask.location.address || ''
        } : {
          coordinates: { lat: null, lng: null },
          address: ''
        },
        images: [],
        followUpOn: ''
      });
    }
    setNewCustomerMode(false);
    setNewCustomerData({ name: '', email: '', phone: '' });
    setValidationErrors({});
  };

  const handleSubmit = async () => {
    const errors = {};
    if (!subtaskForm.description.trim()) {
      errors.description = 'Description is required.';
    }
    if (!subtaskForm.departmentId) {
      errors.departmentId = 'Team is required.';
    }
    if (!subtaskForm.dueDate) {
      errors.dueDate = 'Due date/time is required.';
    }
    // Location is optional, even when assigning field staff.

    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    // Prepare subtask data for submission
    const submitData = {
      description: subtaskForm.description,
      departmentId: subtaskForm.departmentId,
      employeeId: subtaskForm.employeeId && subtaskForm.employeeId.trim() !== '' ? subtaskForm.employeeId : null,
      supervisorId: subtaskForm.supervisorId && subtaskForm.supervisorId.trim() !== '' ? subtaskForm.supervisorId : null,
      priority: subtaskForm.priority,
      dueDate: subtaskForm.dueDate || null,
      supervisorNotes: subtaskForm.supervisorNotes,
      location: subtaskForm.location,
      images: subtaskForm.images,
      followUpOn: subtaskForm.followUpOn || null
    };

    console.log('Subtask creation payload:', submitData); // Debug log

    const result = await onSubmit(submitData);
    if (result.success) {
      resetForm();
      onClose();
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Lock body scroll when modal is open
  useBodyScrollLock(isOpen && !!parentTask);

  // Close on ESC
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    if (isOpen && parentTask) {
      window.addEventListener('keydown', onKeyDown);
    }
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, parentTask]);

  if (!isOpen || !parentTask) return null;

  const filteredSupervisors = getFilteredSupervisors();

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  return (
    <div className="modal-overlay task-modal-overlay subtask-modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content task-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header task-modal-header">
          <div className="task-modal-header-content">
            <div className="task-title-row">
              <span className="task-id-pill">Create Subtask</span>
            </div>
            <p className="modal-subtitle">
              Parent Task: {parentTask.description}
            </p>
          </div>
          <button className="close-button" onClick={handleClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-body task-modal-body">
          {/* Description */}
          <div className="form-row">
            <div className="form-group full-width">
              <label htmlFor="description">Subtask Description <span className="required-asterisk">*</span></label>
              <textarea
                id="description"
                className={`form-control${validationErrors.description ? ' error' : ''}`}
                value={subtaskForm.description}
                onChange={(e) => {
                  if (validationErrors.description) setValidationErrors(prev => ({ ...prev, description: undefined }));
                  setSubtaskForm(prev => ({ ...prev, description: e.target.value }));
                }}
                rows="3"
                placeholder="Enter subtask description"
                autoFocus
              />
              {validationErrors.description && (
                <div className="field-error-message">{validationErrors.description}</div>
              )}
            </div>
          </div>
          <div className="form-row">
            {/* Priority, Due, Follow Up */}
            <div className="form-group full-width">
             {/*  <label>Priority & Timing</label> */}
              <div className="combined-inputs three-cols">
                <div className="mini-field">
                  <label htmlFor="priority">Priority</label>
                  <select
                    id="priority"
                    className="form-control"
                    value={subtaskForm.priority}
                    onChange={(e) => setSubtaskForm(prev => ({ ...prev, priority: e.target.value }))}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
                <div className="mini-field">
                  <DateTimeInput
                    id="dueDate"
                    label="Due Date"
                    required
                    error={validationErrors.dueDate}
                    value={subtaskForm.dueDate}
                    min={toLocalDateTimeInput(new Date())}
                    stepSeconds={60}
                    onClearError={() => {
                      if (validationErrors.dueDate) setValidationErrors(prev => ({ ...prev, dueDate: undefined }));
                    }}
                    onChange={(val) => setSubtaskForm(prev => ({ ...prev, dueDate: val }))}
                  />
                  {validationErrors.dueDate && (
                    <div className="field-error-message">{validationErrors.dueDate}</div>
                  )}
                </div>
                <div className="mini-field">
                  <DateTimeInput
                    id="followUpOn"
                    label="Follow Up On"
                    value={subtaskForm.followUpOn}
                    min={toLocalDateTimeInput(new Date())}
                    stepSeconds={60}
                    onChange={(val) => setSubtaskForm(prev => ({ ...prev, followUpOn: val }))}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="form-row">
            {/* Customer Section - Inherited from parent, read-only */}
            <div className="form-group">
              <label>Customer (Inherited)</label>
              <div className="inherited-customer-info">
                <div className="inherited-item">
                  <span className="inherited-label">Name:</span>
                  <span className="inherited-value">{parentTask.customer?.name || 'N/A'}</span>
                </div>
                <div className="inherited-item">
                  <span className="inherited-label">Email:</span>
                  <span className="inherited-value">{parentTask.customer?.email || 'N/A'}</span>
                </div>
                <div className="inherited-item">
                  <span className="inherited-label">Phone:</span>
                  <span className="inherited-value">{parentTask.customer?.phone || 'N/A'}</span>
                </div>
              </div>
            </div>
            {/* Assignment Group */}
            <div className="form-group">
              <label>Assignment</label>
              <div className="combined-inputs">
                <select
                  id="team"
                  className={`form-control${validationErrors.departmentId ? ' error' : ''}`}
                  value={subtaskForm.departmentId}
                  onChange={(e) => {
                    const departmentId = e.target.value;
                    if (validationErrors.departmentId) setValidationErrors(prev => ({ ...prev, departmentId: undefined }));
                    setSubtaskForm(prev => ({
                      ...prev,
                      departmentId,
                      employeeId: '',
                      supervisorId: ''
                    }));
                  }}
                >
                  <option value="">Select Team *</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
                {validationErrors.departmentId && (
                  <div className="field-error-message">{validationErrors.departmentId}</div>
                )}

                {subtaskForm.departmentId && (
                  <>
                    <select
                      id="supervisor"
                      className="form-control"
                      value={subtaskForm.supervisorId}
                      onChange={(e) => setSubtaskForm(prev => ({ ...prev, supervisorId: e.target.value }))}
                      disabled={isLoadingSupervisors}
                      style={{ color: '#222', background: '#fff' }}
                    >
                      <option value="">
                        {isLoadingSupervisors ? "Loading supervisors..." : "Supervisor (optional)"}
                      </option>
                      {filteredSupervisors.map(supervisor => (
                        <option key={supervisor.id} value={supervisor.id}>
                          {supervisor.firstName} {supervisor.lastName}
                          {supervisor.role === 'admin' ? ' (Admin)' : ''}
                        </option>
                      ))}
                    </select>

                    <select
                      id="employee"
                      className="form-control"
                      value={subtaskForm.employeeId}
                      onChange={(e) => setSubtaskForm(prev => ({ ...prev, employeeId: e.target.value }))}
                      disabled={isLoadingEmployees}
                      style={{ color: '#222', background: '#fff' }}
                    >
                      <option value="">
                        {isLoadingEmployees ? "Loading employees..." : "Field Staff (optional)"}
                      </option>
                      {filteredEmployees.map(employee => (
                        <option key={employee.id} value={employee.id}>
                          {employee.firstName} {employee.lastName} {getDistanceLabel(employee, subtaskForm.location, employeeLocations)}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>
              {subtaskForm.departmentId && !isLoadingSupervisors && filteredSupervisors.length === 0 && (
                <small className="form-text text-muted">No supervisors found for this team</small>
              )}
              {subtaskForm.departmentId && !isLoadingEmployees && filteredEmployees.length === 0 && (
                <small className="form-text text-muted">No active employees found for this team</small>
              )}
            </div>

          </div>

          <div className="form-row">
            {/* Location Section - Inherited from parent but editable */}
            <div className="form-group">
              <label>Location (Inherited from Parent Task)</label>
              <p className="location-note">You can modify the location if needed for this specific subtask.</p>
              <LocationSection
                taskForm={subtaskForm}
                setTaskForm={setSubtaskForm}
                validationErrors={{ location: validationErrors.location }}
                isRequired={false}
              />
            </div>

            {/* Image Upload */}
            <ImageUpload
              taskForm={subtaskForm}
              setTaskForm={setSubtaskForm}
              isSubmitting={isSubmitting}
            />
          </div>

          {/* Supervisor Notes */}
         {/*  <div className="form-group">
            <label htmlFor="supervisorNotes">Supervisor Notes</label>
            <textarea
              id="supervisorNotes"
              className="form-control"
              value={subtaskForm.supervisorNotes}
              onChange={(e) => setSubtaskForm(prev => ({ ...prev, supervisorNotes: e.target.value }))}
              rows="3"
              placeholder="Add any special instructions or notes for this subtask..."
            />
          </div> */}



        </div>

        <div className="modal-footer task-modal-footer">
          <button
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            className="submit-button"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creating...' : 'Create Subtask'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateSubtaskModal; 